import { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/shared/api/queryKeys';
import { notificationsApi, notificationTemplatesApi, notificationEventsApi } from '../api/notifications.api';
import { findFallbackTemplate } from '../fallback/notificationTemplates';
import { FALLBACK_EVENTS } from '../notifications.meta';

export function useNotifications(depId) {
  return useQuery({
    queryKey: queryKeys.notifications.all(depId),
    queryFn: () => notificationsApi.list(depId),
    enabled: !!depId,
    staleTime: 30_000,
  });
}

export function useCreateNotification(depId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) => notificationsApi.create(depId, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.notifications.all(depId) }),
  });
}

export function useUpdateNotification(depId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }) => notificationsApi.update(depId, id, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.notifications.all(depId) }),
  });
}

export function useDeleteNotification(depId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => notificationsApi.delete(depId, id),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.notifications.all(depId) }),
  });
}

export function useToggleNotification(depId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, enabled }) => notificationsApi.toggle(depId, id, enabled),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.notifications.all(depId) }),
  });
}

/**
 * Resolves THE template for an (event, channel) pair. Each event has exactly one
 * template per channel on the BE, so picking the event implicitly picks the
 * template. Falls back to the bundled sample when the BE returns none, so the
 * preview works before the BE ships templates.
 */
export function useResolvedTemplate(event, channel) {
  const query = useQuery({
    queryKey: queryKeys.notificationTemplates.resolve(event, channel),
    queryFn: () => notificationTemplatesApi.list({ event, channel }),
    enabled: !!event && !!channel,
    staleTime: 5 * 60_000,
  });

  const beList = query.data ?? [];
  const beTemplate = beList[0] ?? null;
  const template = beTemplate ?? (event && channel ? findFallbackTemplate(event, channel) : null);
  const usingFallback = !query.isLoading && !beTemplate && !!template;

  return { ...query, template, usingFallback };
}

/**
 * Catalog of trigger events from the BE (GET /notification-events). Falls back
 * to the bundled FALLBACK_EVENTS so the form works before the BE ships them.
 *
 * Returns:
 *  - events:  [{ key, label, group }] (BE list or fallback)
 *  - groups:  ordered, de-duped group names present in the list
 *  - byKey:   { [key]: { key, label, group } } for label lookups (table rows)
 *  - usingFallback: true when the BE returned nothing
 */
export function useNotificationEvents() {
  const query = useQuery({
    queryKey: queryKeys.notificationEvents.all(),
    queryFn: () => notificationEventsApi.list(),
    staleTime: 5 * 60_000,
  });

  const beList = query.data ?? [];
  const events = beList.length > 0 ? beList : FALLBACK_EVENTS;
  const usingFallback = !query.isLoading && beList.length === 0;

  const groups = useMemo(() => {
    const seen = new Set();
    const out = [];
    for (const e of events) {
      if (e.group && !seen.has(e.group)) { seen.add(e.group); out.push(e.group); }
    }
    return out;
  }, [events]);

  const byKey = useMemo(() => Object.fromEntries(events.map((e) => [e.key, e])), [events]);

  return { ...query, events, groups, byKey, usingFallback };
}
